import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AcreageReportService } from '../../../core/services/acreage-report.service';
import { ChemicalReportService } from '../../../core/services/chemical-report.service';
import {
  AcreageReportOrgDetail
} from '../../../core/models/acreage-report.model';
import {
  ChemicalReportOrgDetail
} from '../../../core/models/chemical-report.model';

/**
 * Organization Reports Tab Component
 *
 * Displays inline acreage and chemical usage reports for a specific organization.
 * Includes date range filter and PDF export buttons for each report type.
 */
@Component({
  selector: 'app-organization-reports-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './organization-reports-tab.component.html',
  styleUrls: ['./organization-reports-tab.component.scss']
})
export class OrganizationReportsTabComponent implements OnInit {
  @Input() organizationId!: string;

  loading = true;
  errorMessage = '';

  // Date range filter
  filterStartDate = '';
  filterEndDate = '';

  // Report data
  acreageData: AcreageReportOrgDetail | null = null;
  chemicalData: ChemicalReportOrgDetail | null = null;

  // Export loading states
  exportingAcreage = false;
  exportingChemical = false;

  constructor(
    private acreageReportService: AcreageReportService,
    private chemicalReportService: ChemicalReportService
  ) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.initializeDateRange();
    this.loadReportData();
  }

  /**
   * Initialize date range to current month (first and last day)
   */
  private initializeDateRange(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.filterStartDate = this.formatDateForInput(firstDay);
    this.filterEndDate = this.formatDateForInput(lastDay);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.loadReportData();
  }

  /**
   * Load both acreage and chemical report data
   */
  async loadReportData(): Promise<void> {
    if (!this.filterStartDate || !this.filterEndDate) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      // Load both reports in parallel
      const [acreage, chemical] = await Promise.all([
        this.acreageReportService.getOrganizationDetail(
          this.organizationId,
          this.filterStartDate,
          this.filterEndDate
        ),
        this.chemicalReportService.getOrganizationDetail(
          this.organizationId,
          this.filterStartDate,
          this.filterEndDate
        )
      ]);

      this.acreageData = acreage;
      this.chemicalData = chemical;
    } catch (error: any) {
      console.error('Failed to load report data:', error);
      this.errorMessage = 'Failed to load report data. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Export acreage report to PDF
   */
  async exportAcreagePdf(): Promise<void> {
    if (!this.acreageData || this.exportingAcreage) return;

    this.exportingAcreage = true;
    this.errorMessage = '';

    try {
      const filters = {
        startDate: this.filterStartDate,
        endDate: this.filterEndDate,
        organizationId: this.organizationId
      };

      const result = await this.acreageReportService.exportPdf(
        'organization-detail',
        filters,
        this.acreageData
      );

      if (result && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to export acreage PDF:', error);
      this.errorMessage = 'Failed to export acreage PDF';
    } finally {
      this.exportingAcreage = false;
    }
  }

  /**
   * Export chemical report to PDF
   */
  async exportChemicalPdf(): Promise<void> {
    if (!this.chemicalData || this.exportingChemical) return;

    this.exportingChemical = true;
    this.errorMessage = '';

    try {
      const filters = {
        startDate: this.filterStartDate,
        endDate: this.filterEndDate,
        organizationId: this.organizationId
      };

      const result = await this.chemicalReportService.exportPdf(
        'organization-detail',
        filters,
        this.chemicalData
      );

      if (result && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to export chemical PDF:', error);
      this.errorMessage = 'Failed to export chemical PDF';
    } finally {
      this.exportingChemical = false;
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
}
